import React from 'react';
import styles from './index.module.scss';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type PageItem = number | 'left-ellipsis' | 'right-ellipsis';
interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    maxVisiblePages?: number;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange, maxVisiblePages = 5 }) => {
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    const handlePrevious = () => !isFirstPage && onPageChange(currentPage - 1);
    const handleNext = () => !isLastPage && onPageChange(currentPage + 1);

    const getPageNumbers = (): PageItem[] => {
        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const pages: PageItem[] = [1];

        let middleStart = currentPage - Math.floor((maxVisiblePages - 2) / 2);
        let middleEnd = currentPage + Math.floor((maxVisiblePages - 2) / 2);

        if (middleStart < 2) {
            middleStart = 2;
            middleEnd = maxVisiblePages - 1;
        }
        else if (middleEnd > totalPages - 1) {
            middleEnd = totalPages - 1;
            middleStart = totalPages - maxVisiblePages + 2;
        }

        if (middleStart > 2) {
            pages.push('left-ellipsis');
        }

        for (let i = middleStart; i <= middleEnd; i++) {
            pages.push(i);
        }

        if (middleEnd < totalPages - 1) {
            pages.push('right-ellipsis');
        }

        pages.push(totalPages);

        return pages;
    };

    const handleEllipsisClick = (type: 'left-ellipsis' | 'right-ellipsis') => {
        if (type === 'left-ellipsis') {
            onPageChange(Math.max(1, currentPage - (maxVisiblePages - 1)));
        } else {
            onPageChange(Math.min(totalPages, currentPage + (maxVisiblePages - 1)));
        }
    };

    return (
        <div className={styles.pagination} aria-label="Pagination">
            <div className={styles.totalPages}>
                <span>Showing <b>{currentPage}</b> to <b>{totalPages}</b> entries</span>
            </div>
            <div className={styles.navButtons}>
                <button
                    className={styles.navBox}
                    onClick={handlePrevious}
                    disabled={isFirstPage}
                    aria-label="Previous Page"
                >
                    <ChevronLeft size={16} />
                </button>
                {getPageNumbers().map((item, index) => {
                    if (item === 'left-ellipsis' || item === 'right-ellipsis') {
                        return (
                            <button
                                key={`${item}-${index}`}
                                className={`${styles.ellipsis} ${styles.navBox}`}
                                onClick={() => handleEllipsisClick(item)}
                                aria-label={`Jump ${item === 'left-ellipsis' ? 'backward' : 'forward'}`}
                            >
                                ...
                            </button>
                        );
                    }
                    return (
                        <button
                            key={item}
                            className={`${styles.navBox} ${currentPage === item ? styles.active : ''
                                }`}
                            onClick={() => onPageChange(item)}
                            aria-current={currentPage === item ? 'page' : undefined}
                        >
                            {item}
                        </button>
                    );
                })}
                <button
                    className={styles.navBox}
                    onClick={handleNext}
                    disabled={isLastPage}
                    aria-label="Next Page"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default Pagination;